import { useFlag } from '@reflag/react-sdk';
import { skipToken, useQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { SiteVisibility } from '@gitbook/api-client';
import { extractDeterministicJSONSchemaFromVisitorSchema } from '@gitbook/app-adaptive-content';
import { inferDefaultInputValuesFromObjectJSONSchema, SymbolsTable } from '@gitbook/expr';

import { apiQuery } from '../../state';
import { ExpressionProvider } from '../Expressions';
import { DocumentationLink } from '../Link';
import { useSpacePrimarySite } from '../Organization';

type SiteExpressionContextProviderProps =
    | { organizationId: api.Organization['id']; site: api.Site }
    | { space: api.Space };

/**
 * Provide site visitor specific adaptive content context to the expression editors.
 */
export function SiteVisitorExpressionContextProvider(
    props: React.PropsWithChildren<SiteExpressionContextProviderProps>
) {
    return 'site' in props ? (
        <SiteExpressionContextProviderWithSite {...props} />
    ) : (
        <SiteExpressionContextProviderWithSpace {...props} />
    );
}

/**
 * Provide site visitor specific adaptive content context to the condition expression editor in a site.
 *
 * Doesn't suspend to avoid blocking the rendering of the site.
 */
function SiteExpressionContextProviderWithSite(
    props: React.PropsWithChildren<{
        organizationId: api.Organization['id'];
        site: api.Site;
    }>
) {
    const { organizationId, site, children } = props;

    const hasAdaptiveContentEnabled = Boolean(site.adaptiveContent?.enabled);
    const hasVisitorTypeEnabled = useFlag('ADAPTIVE_VISITOR_TYPE').isEnabled;
    const hasRegionEnabled = useFlag('ADAPTIVE_CONTENT_REGION').isEnabled;

    const { data: siteSchema } = useQuery(
        apiQuery(
            'getSiteAdaptiveSchema',
            hasAdaptiveContentEnabled ? { organizationId, siteId: site.id } : skipToken,
            { nullOnError: true }
        )
    );

    const expressionEditorContext = React.useMemo(() => {
        const jsonSchema = siteSchema?.jsonSchema ?? {
            type: 'object',
            properties: {},
            additionalProperties: false,
        };
        const symbolsOptions = {
            includeVisitorType: hasVisitorTypeEnabled,
            regionNames: hasRegionEnabled ? Object.keys(siteSchema?.jsonSchema.regions ?? {}) : [],
        };
        const symbolsTable = getSymbolTableFromSiteJSONSchema(jsonSchema, symbolsOptions);
        const dynamicPropChecker = new DynamicPropChecker(symbolsTable, jsonSchema, symbolsOptions);
        return {
            symbolsTable,
            autocompleteFilter: (symbolPath: string | string[]) => {
                return dynamicPropChecker.isDynamicProperty(symbolPath)
                    ? {
                          exclusionMessage: (
                              <>
                                  Dynamic properties can't be used in this expression.{' '}
                                  <DocumentationLink
                                      path="publishing-documentation/adaptive-content"
                                      host="docs"
                                  >
                                      Learn more
                                  </DocumentationLink>
                              </>
                          ),
                      }
                    : false;
            },
        };
    }, [siteSchema, hasVisitorTypeEnabled, hasRegionEnabled]);

    if (!hasAdaptiveContentEnabled) {
        return <>{children}</>;
    }

    return <ExpressionProvider {...expressionEditorContext}>{children}</ExpressionProvider>;
}

/**
 * Provide site visitor specific adaptive content context to the inline expression editor in a space.
 *
 * Doesn't suspend to avoid blocking the rendering of the space.
 */
function SiteExpressionContextProviderWithSpace(
    props: React.PropsWithChildren<{ space: api.Space }>
) {
    const { space, children } = props;

    const spacePrimarySiteLookup = useSpacePrimarySite();
    const site = spacePrimarySiteLookup?.site;
    const hasAdaptiveContentEnabled = site && Boolean(site.adaptiveContent?.enabled);
    const hasVisitorTypeEnabled = useFlag('ADAPTIVE_VISITOR_TYPE').isEnabled;
    const hasRegionEnabled = useFlag('ADAPTIVE_CONTENT_REGION').isEnabled;

    const { data: siteSchema } = useQuery(
        apiQuery(
            'getSiteAdaptiveSchema',
            hasAdaptiveContentEnabled
                ? { organizationId: space.organization, siteId: site.id }
                : skipToken,
            { nullOnError: true }
        )
    );

    const expressionEditorContext = React.useMemo(() => {
        const jsonSchema = siteSchema?.jsonSchema ?? {
            type: 'object',
            properties: {},
            additionalProperties: false,
        };

        const symbolsOptions = {
            includeVisitorType: hasVisitorTypeEnabled,
            regionNames: hasRegionEnabled ? Object.keys(siteSchema?.jsonSchema.regions ?? {}) : [],
        };
        const symbolsTable = getSymbolTableFromSiteJSONSchema(jsonSchema, symbolsOptions);
        const dynamicPropChecker = new DynamicPropChecker(symbolsTable, jsonSchema, symbolsOptions);

        return {
            inputValues: {
                visitor: {
                    ...(hasVisitorTypeEnabled ? { type: 'human' } : {}),
                    claims: inferDefaultInputValuesFromObjectJSONSchema(jsonSchema),
                    isSet: Boolean(siteSchema),
                    // Only a visitor-authenticated site has every visitor signed in; anywhere else
                    // the anonymous visitor is the one to preview by default.
                    isAuthenticated: site?.visibility === SiteVisibility.VisitorAuth,
                },
            },
            symbolsTable,
            autocompleteFilter: (symbolPath: string | string[]) => {
                return dynamicPropChecker.isDynamicProperty(symbolPath)
                    ? {
                          exclusionMessage: (
                              <>
                                  Dynamic properties can't be used in this expression.{' '}
                                  <DocumentationLink
                                      path="publishing-documentation/adaptive-content"
                                      host="docs"
                                  >
                                      Learn more
                                  </DocumentationLink>
                              </>
                          ),
                      }
                    : false;
            },
        };
    }, [site?.visibility, siteSchema, hasVisitorTypeEnabled, hasRegionEnabled]);

    if (!hasAdaptiveContentEnabled) {
        return <>{children}</>;
    }

    return <ExpressionProvider {...expressionEditorContext}>{children}</ExpressionProvider>;
}

type VisitorSymbolsOptions = {
    includeVisitorType: boolean;
    /** Names of the site regions, exposed as `visitor.region` when not empty. */
    regionNames: string[];
};

function getSymbolTableFromSiteJSONSchema(
    siteJSONSchema: api.SiteAdaptiveJSONSchema,
    options: VisitorSymbolsOptions
) {
    const { includeVisitorType, regionNames } = options;
    return new SymbolsTable({
        visitor: SymbolsTable.inferSymbolFromJSONSchema(
            {
                type: 'object',
                description: `The attributes tied to the site's visitor.`,
                properties: {
                    ...(includeVisitorType
                        ? {
                              type: {
                                  type: 'string',
                                  enum: ['human', 'agent'],
                                  description: 'Whether the visitor is a human or an AI agent.',
                              },
                          }
                        : {}),
                    ...(regionNames.length > 0
                        ? {
                              region: {
                                  type: 'string',
                                  enum: regionNames,
                                  description:
                                      "Region of the visitor, resolved from their country. Undefined when the country isn't in any region.",
                              },
                          }
                        : {}),
                    claims: {
                        ...siteJSONSchema,
                        description: 'The attributes tied set on the site visitor.',
                    },
                    isAuthenticated: {
                        type: 'boolean',
                        description:
                            'Whether the visitor is signed in, verified from the token they presented.',
                    },
                    isSet: {
                        type: 'boolean',
                        description:
                            'Deprecated: whether the visitor has any claim set, including unsigned ones. Use isAuthenticated to tell whether a visitor is signed in.',
                    },
                },
                required: includeVisitorType
                    ? ['type', 'claims', 'isSet', 'isAuthenticated']
                    : ['claims', 'isSet', 'isAuthenticated'],
            },
            'visitor'
        ),
    });
}

/**
 * Wrapper around a symbol table that provides utility to check if a symbol is a dynamic (non-deterministic) property.
 */
class DynamicPropChecker {
    private fullSchemaSymbolTable: SymbolsTable;
    private deterministicSymbolsTable: SymbolsTable;

    constructor(
        fullSchemaSymbolTable: SymbolsTable,
        siteJSONSchema: api.SiteAdaptiveJSONSchema,
        options: VisitorSymbolsOptions
    ) {
        this.fullSchemaSymbolTable = fullSchemaSymbolTable;
        this.deterministicSymbolsTable = getSymbolTableFromSiteJSONSchema(
            extractDeterministicJSONSchemaFromVisitorSchema(siteJSONSchema),
            options
        );
    }

    /**
     * Returns true if a symbol at a given path corresponds to a dynamic schema property.
     */
    isDynamicProperty(path: string | string[]): boolean {
        const baseSymbol = this.fullSchemaSymbolTable.getSymbolInfo(path);
        if (!baseSymbol) {
            return false;
        }

        const deterministicSymbol = this.deterministicSymbolsTable.getSymbolInfo(path);
        return deterministicSymbol === undefined;
    }
}
